import React, { useEffect, useRef } from "react";
import { Webchat, WebchatProvider, getClient, UploadButton, Composer } from "@botpress/webchat";


import { buildTheme } from "@botpress/webchat-generator";

const clientId = "d0b6d2e2-5509-4354-8095-0c9c37922bdc";

// Build Theme
const { theme, style } = buildTheme({
  themeName: "prism",
  themeColor: "rgb(110, 113, 115)",
  color: "rgb(238, 15, 15)",
});

// Webchat Configuration
const config = {
  composerPlaceholder: "Escreva aqui",
  botName: "Ellev",
  botAvatar: "https://files.bpcontent.cloud/2025/01/14/18/20250114183948-MAZTCHVA.png",
  botDescription: "Olá! 👋 Sou o Ellev, seu parceiro de estratégia! Pronto para desenvolver seu projeto?",
};

// Custom CSS Overrides for Styling
const extraStyles = `

.bpHeaderContainer {
  display: none
}

.bpContainer {
  border: none !important;
  box-shadow: none !important;
  display: flex !important;
  flex-direction: column !important;
  justify-content: flex-end !important; /* ✅ Aligns chat to the bottom */
  height: 100% !important;
}

.bpMessageListContainer {
  flex-grow: 1 !important;
  display: flex !important;
  flex-direction: column !important;
  justify-content: flex-end !important; /* ✅ Ensures messages start at the bottom */
}

.bpModalDialogNewConversationButton {
    background-color: #FFFFFF;
    color: #606268;
    border: 1px solid  #606268;
}

.bpModalDialogNewConversationButton:hover {
    background-color: #f0f0f0;
    color: #333333; 
    border: 1px solid #333333;
}

.bpComposerContainer {
    height: 85px !important;
    width: 100%;
    margin: 0 auto;
}

.bpHeaderContainer svg[viewBox="0 0 24 24"] {
    display: none !important;
}

  /* Header: Make it Dark Blue */
  .bpComposerButtonIcon   {
    color: #606268;
  }
  .bpHeaderContentTitle, .bpHeaderContentDescription, .bpHeaderContentActionsIcons {
    color: #606268;
  } 

.bpMessageContainer[data-direction="outgoing"] .bpMessageBlocksBubble, .bpHeaderContainer, .bpComposerContainer {
    background-color: rgb(242 239 239) !important;
    color: #606268 !important;
    border-radius: 10px;
}
  .bpMessageAvatarContainer img, .bpMessageListMarqueeAvatarContainer img {
      border: 1px solid rgb(242 239 239) !important; /* Cinza claro igual ao ChatGPT */
      border-radius: 50% !important; /* Borda arredondada */
      padding: 2px;
      background-color: #F9FAFB; /* Fundo leve para destacar o avatar */
  }

  .bpMessageContainer[data-direction="incoming"] .bpMessageBlocksBubble {
    background-color: #FFFFFF !important;
    color: #606268 !important;
}

.bpMessageContainer[data-direction="incoming"] .bpMessageBlocksButton {
    background-color: #FFFFFF;
    color: #606268;
    border: 1px solid  #606268;
}

.bpMessageContainer[data-direction="incoming"] .bpMessageBlocksButton:hover {
    background-color: #f0f0f0;
    color: #333333; 
    border: 1px solid  #606268;
}
  
.bpMessageContainer[data-direction="incoming"] .bpMessageBlocksDropdownButtonContainer:hover {
    background-color: #f0f0f0;
    color: #333333; 
    border: 1px solid  #606268;
}

  /* Table Styling */
  .bpMessageBlocksBubble table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    margin: 1px 1px;
    font-family: 'Montserrat', Arial, sans-serif;
    font-size: 12px;
    line-height: 1.2;
    color: #2A3B4B;
    border-radius: 10px; /* Rounded corners */
    overflow: hidden; /* Ensures border-radius applies correctly */
  }

  .bpMessageBlocksBubble th,
  .bpMessageBlocksBubble td {
    border: 1px solid #B2B2B2;
    padding: 10px; /* More padding for better readability */
    text-align: center;
  }

  .bpMessageBlocksBubble th {
    background-color:rgba(42, 59, 75, 0.79);
    color: #FFFFFF;
    font-weight: bold;
    font-size: 10px;
    text-transform: uppercase;
  }

  .bpMessageBlocksBubble tr:first-child th:first-child {
    border-top-left-radius: 10px;
  }

  .bpMessageBlocksBubble tr:first-child th:last-child {
    border-top-right-radius: 10px;
  }

  .bpMessageBlocksBubble tr:last-child td:first-child {
    border-bottom-left-radius: 10px;
  }

  .bpMessageBlocksBubble tr:last-child td:last-child {
    border-bottom-right-radius: 10px;
  }

  .bpMessageBlocksBubble tr:nth-child(even) {
    background-color:rgb(218, 229, 240);
  }

  .bpMessageBlocksBubble tr:nth-child(odd) {
    background-color: #DCDCDC;
  }

  .bpMessageBlocksBubble tr:hover {
    background-color:rgb(255, 236, 199);
    color: #2A3B4B;
  }

  .bpMessageBlocksBubble caption {
    caption-side: bottom;
    font-size: 12px;
    color: #666666;
    padding-top: 8px;
    text-align: center;
  }
`;

export default function App() {
  const client = getClient({ clientId });
  const processedEvents = useRef(new Set()); // ✅ Prevents duplicate event processing

  useEffect(() => {
    console.log("✅ React Webchat Initialized");

    function handleConversation(conversationId) {
      console.log("📩 New Conversation Started:", conversationId);
      processedEvents.current.clear();
      window.parent.postMessage(
        { type: "conversation-started", conversationId },
        "*"
      );
    }

    function handleMessage(event) {
      if (!event.data) return;

      if (!["restart-conversation", "next", "save"].includes(event.data.type)) {
        if (processedEvents.current.has(event.data.userId)) return;
        processedEvents.current.add(event.data.userId);
      }

      if (event.data.type === "event-sent") {
        console.log("📩 Received event from parent:", event.data);
        client.sendEvent({
          type: "event-sent",
          userName: event.data.userName,
          userId: event.data.userId,
        });
        console.log("🚀 Sent event to Botpress:", event.data);
      }

      if (event.data.type === "restart-conversation") {
        console.log("✅ Restarting conversation...");
        client.newConversation();
      }

      if (event.data.type === "next") {
        console.log("✅ Triggering 'next' event...");
        client.sendEvent({ type: "event-next" });
      }

      if (event.data.type === "save") {
        console.log("✅ Triggering 'save' event...");
        client.sendEvent({ type: "event-save" });
      }
    }

    // ✅ Store the custom event handler in a variable
    const handleCustomEvent = (event) => {
      console.log("Custom Event detected from Botpress to React!", event);
      window.parent.postMessage(
        { type: "customEvent", eventData: event },
        "*"
      );
    };

    // ✅ Add event listeners
    client.on("conversation", handleConversation);
    window.addEventListener("message", handleMessage);
    client.on("customEvent", handleCustomEvent);

    // ✅ Cleanup function to properly remove listeners
    return () => {
      console.log("🛑 Cleaning up event listeners...");
      client.off("conversation", handleConversation);
      window.removeEventListener("message", handleMessage);
      client.off("customEvent", handleCustomEvent); // ✅ Properly remove customEvent listener
    };
  }, [client]);
  

  return (
    <WebchatProvider key={JSON.stringify(config)} theme={theme} configuration={config} client={client}>
      <style>{style}</style>
      <style>{extraStyles}</style> {/* Aplicar estilos extras */}
      
      <div style={styles.container}>
        <div style={styles.chatContainer}>
          <Webchat />
          <Composer>
            <UploadButton />
          </Composer>
        </div>
      </div>
    </WebchatProvider>
  );
}

// Styles
const styles = {
  container: {
    width: "100vw",
    height: "100vh",
    backgroundColor: "#FFFFFF",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  chatContainer: {
    width: "100%",
    height: "100vh",
    borderRadius: "10px",
    overflow: "hidden",
  },
};
